import { useMe } from '../../organisation/useMe';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';
import { getAttributeMappingFieldName } from '../../common/helper';
import { ApiAttributeMappingFieldEnum } from '../../api';
import { Direction } from '../../common/attributes/types';
import { clsx } from 'clsx';
import { formatIncomingAmount, formatOutgoingAmount, getDisplayValue } from '../../schema/helper';
import { InternalApiEntitySchemaField, InternalApiEntitySchemaFieldType } from '../../api-internal';
import React from 'react';

interface SimpleValueProps {
  fieldName: string;
  fieldValue: string;
  attributes: { [key: string]: string };
  schemaField: InternalApiEntitySchemaField;
}

export const SimpleValue = ({ fieldName, fieldValue, schemaField, attributes }: SimpleValueProps) => {
  const { currentOrganisation } = useMe();
  const currency = currentOrganisation!.currency;

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const direction = attributes[
    getAttributeMappingFieldName(ApiAttributeMappingFieldEnum.CREDIT, attributeMappings.map)
  ] as Direction;

  switch (fieldName) {
    case 'status': {
      return (
        <span
          className={clsx({
            rejected: fieldValue === 'Rejected',
            cancelled: fieldValue === 'Cancelled',
          })}
        >
          {getDisplayValue(schemaField, fieldValue)}
        </span>
      );
    }
    case 'amount': {
      return (
        <span
          className={clsx({
            ['amount-outgoing']: direction === 'O',
          })}
        >
          {direction === 'I'
            ? formatIncomingAmount(Number(fieldValue), currency)
            : direction === 'O'
              ? formatOutgoingAmount(Number(fieldValue), currency)
              : getDisplayValue(schemaField, fieldValue)}
        </span>
      );
    }
    default: {
      if (schemaField?.type === InternalApiEntitySchemaFieldType.DATETIME) {
        return <span style={{ whiteSpace: 'nowrap' }}>{getDisplayValue(schemaField, fieldValue)}</span>;
      } else {
        return getDisplayValue(schemaField, fieldValue);
      }
    }
  }
};
